import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  BarChart,
  Bar,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import carImage from "../assets/images/img-1.png";
import usersImage from "../assets/images/img-2.png";
import buildingImage from "../assets/images/img-3.png";
import locationImage from "../assets/images/img-4.png";
import { apiCall } from "../Apis/apiCall";
import Breadcrumb from "../components/Breadcrumb";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { handleApiResponse } from "../utils/response";
import CustomSelect from "../components/common/CustomSelect";
import ErrorAlert from "../components/ui/ErrorAlert";
import { MapContainer, TileLayer, Marker, Popup, Polygon } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Shimmer components for loading states
// Replace your current ShimmerCard and ShimmerChart with these

const ShimmerCard = () => (
  <div className="card border-0 shadow-sm radius-12 h-100">
    <div className="card-body p-20">
      <div className="d-flex align-items-center gap-3">
        {/* Icon Placeholder */}
        <div className="placeholder-glow">
          <div className="placeholder w-60-px h-60-px rounded-circle"></div>
        </div>
        {/* Text Placeholders */}
        <div className="flex-grow-1">
          <div className="placeholder-glow mb-2">
            <span className="placeholder col-9"></span>
          </div>
          <div className="placeholder-glow">
            <span className="placeholder col-6"></span>
          </div>
          <div className="placeholder-glow mt-2">
            <span className="placeholder col-7"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ShimmerChart = () => (
  <div className="card border-0 shadow-sm radius-12 h-100">
    <div className="card-body p-24">
      {/* Title Placeholder */}
      <div className="placeholder-glow mb-20">
        <span className="placeholder col-4 h-24-px rounded"></span>
      </div>
      {/* Chart Area Placeholder */}
      <div className="placeholder-glow">
        <div 
          className="placeholder w-100 h-300-px rounded-12"
          style={{ minHeight: "320px" }}
        ></div>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  // Individual loading states
  const [cardsLoading, setCardsLoading] = useState(false);
  const [occupancyLoading, setOccupancyLoading] = useState(false);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [locationMapData, setLocationMapData] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  const [counts, setCounts] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [staffData, setStaffData] = useState([]);
  const [error, setError] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("$");
  const [occupancyData, setOccupancyData] = useState({
    lotData: [],
    hourlyData: [],
  });
  const [revenueData, setRevenueData] = useState({
    paymentTypes: { total_revenue: 0, breakdown: [] },
    dailyTrend: [],
  });

  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    navigateTo: "",
    showCancel: true,
  });

  const [timeFrame, setTimeFrame] = useState("today");
  const [activeOccupancyTab, setActiveOccupancyTab] = useState("byLot");
  const [activeRevenueTab, setActiveRevenueTab] = useState("paymentTypes");

  const navigate = useNavigate();

  const timeFrameOptions = [
    { label: "Today", value: "today" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
  ];

  // Initial data fetch on component mount
  useEffect(() => {
    fetchAllInitialData();
  }, []);

  // Fetch cards data when timeFrame changes
  useEffect(() => {
    if (timeFrame) {
      fetchCardsData();
    }
  }, [timeFrame]);

  // Fetch occupancy data when tab or timeFrame changes
  // useEffect(() => {
  //   if (activeOccupancyTab && timeFrame) {
  //     fetchOccupancyData();
  //   }
  // }, [activeOccupancyTab, timeFrame]);
  useEffect(() => {
    if (activeOccupancyTab) {
      fetchOccupancyData();
    }
  }, [activeOccupancyTab]);

  // Fetch revenue data when tab or timeFrame changes
  // useEffect(() => {
  //   if (activeRevenueTab && timeFrame) {
  //     fetchRevenueData();
  //   }
  // }, [activeRevenueTab, timeFrame]);
  useEffect(() => {
    if (activeRevenueTab) {
      fetchRevenueData();
    }
  }, [activeRevenueTab]);

  const fetchAllInitialData = async () => {
    try {
      setCardsLoading(true);
      setStaffLoading(true);

      const res = await apiCall(
        "get",
        `/get-dashboard-counts?keys[]=initial_data&keys[]=staff_performance`
      );

      setCounts(res.data.data);
      setInitialData(res.data.initial_data);
      setStaffData(res.data.staff_performance || []);
      setCurrencyCode(res.data.data.currency_code);

      checkForZeroValues(res.data.data);
      handleApiResponse(res.data, "All Data Fetched Successfully", "GET");
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to Fetch Data", "GET");
      setError(true);
    } finally {
      setCardsLoading(false);
      setStaffLoading(false);
    }
  };

  const fetchCardsData = async () => {
    setCardsLoading(true);
    try {
      const { from_date, to_date } = getDateRange();

      const res = await apiCall(
        "get",
        `/get-dashboard-counts?keys[]=initial_data&from_date=${from_date}&to_date=${to_date}&keys[]=staff_performance`
      );

      setInitialData(res.data.initial_data);
      setCurrencyCode(res.data.data.currency_code);
      setStaffData(res.data.staff_performance || []);
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to Fetch Cards Data", "GET");

      // Fallback data
      setInitialData({
        vehicles_parked: 45,
        current_occupancy: 68,
        active_sessions: 12,
        revenue_generated: [{ total: 1250 }],
      });
    } finally {
      setCardsLoading(false);
    }
  };

  const fetchOccupancyData = async () => {
    setOccupancyLoading(true);
    try {
      const { from_date, to_date } = getDateRange();
      const occupancyMode =
        activeOccupancyTab === "byLot" ? "by_location" : "hourly_trend";

      const res = await apiCall(
        "get",
        `/get-dashboard-counts?keys[]=occupancy_overview&occupancy_mode=${occupancyMode}`
      );

      // Process occupancy data
      if (occupancyMode === "by_location") {
        const lotData =
          res.data.occupancy_overview?.by_location?.map((item) => ({
            name: item.location_name,
            occupied: item.occupied_slots,
            total: item.created_slots,
            percentage: Math.round(item.occupancy_percent),
          })) || [];
        setOccupancyData((prev) => ({ ...prev, lotData }));
      } else if (occupancyMode === "hourly_trend") {
        const totalSlots = res.data.data?.slots_created || 943;
        const hourlyMap = {};
        res.data.occupancy_overview?.hourly_trend?.forEach((item) => {
          const hour = new Date(item.time).getHours();
          if (!hourlyMap[hour]) {
            hourlyMap[hour] = { vehicles_started: 0, count: 0 };
          }
          hourlyMap[hour].vehicles_started += item.vehicles_started;
          hourlyMap[hour].count += 1;
        });

        const hourlyData = Array.from({ length: 24 }, (_, i) => {
          const hour = i;
          const data = hourlyMap[hour] || { vehicles_started: 0, count: 0 };
          const vehicles = data.vehicles_started;
          return {
            hour: `${hour}h`,
            value: vehicles,
            isPeak: vehicles >= 5,
          };
        });
        setOccupancyData((prev) => ({ ...prev, hourlyData }));
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to Fetch Occupancy Data", "GET");

      // Fallback data
      if (activeOccupancyTab === "byLot") {
        setOccupancyData((prev) => ({
          ...prev,
          lotData: [
            {
              name: "Lot A - Main Street",
              occupied: 18,
              total: 25,
              percentage: 72,
            },
            {
              name: "Lot B - Shopping Center",
              occupied: 22,
              total: 30,
              percentage: 73,
            },
            {
              name: "Lot C - Office Complex",
              occupied: 7,
              total: 20,
              percentage: 35,
            },
          ],
        }));
      } else {
        setOccupancyData((prev) => ({
          ...prev,
          hourlyData: Array.from({ length: 24 }, (_, i) => ({
            hour: `${i}h`,
            value: Math.floor(Math.random() * 20),
            isPeak: i >= 8 && i <= 18,
          })),
        }));
      }
    } finally {
      setOccupancyLoading(false);
    }
  };

  const fetchLocationMapData = async () => {
    setMapLoading(true);
    try {
      const res = await apiCall(
        "get",
        `/get-all-branch-locations?want_pagination=0`
      );

      // Process locations - extract center point from polygons
      const processedLocations = (res.data.data || [])
        .map((loc) => {
          if (loc.polygons && loc.polygons.length > 0) {
            // Calculate center of polygon
            const sumLat = loc.polygons.reduce(
              (sum, point) => sum + point.lat,
              0
            );
            const sumLng = loc.polygons.reduce(
              (sum, point) => sum + point.lng,
              0
            );
            const centerLat = sumLat / loc.polygons.length;
            const centerLng = sumLng / loc.polygons.length;

            return {
              ...loc,
              latitude: centerLat,
              longitude: centerLng,
            };
          }
          return null;
        })
        .filter((loc) => loc !== null);

      setLocationMapData(processedLocations);
    } catch (error) {
      handleApiResponse(
        error.response?.data,
        "Failed to Fetch Map Data",
        "GET"
      );
      setLocationMapData([]);
    } finally {
      setMapLoading(false);
    }
  };

  const fetchRevenueData = async () => {
    setRevenueLoading(true);
    try {
      const { from_date, to_date } = getDateRange();
      const revenueMode =
        activeRevenueTab === "paymentTypes" ? "payment_types" : "daily_trend";

      const res = await apiCall(
        "get",
        `/get-dashboard-counts?keys[]=revenue_analytics&revenue_mode=${revenueMode}`
      );

      // Process revenue data
      if (revenueMode === "payment_types") {
        const paymentTypes = res.data.revenue_analytics?.payment_types || {
          total_revenue: 0,
          breakdown: [],
        };
        setRevenueData((prev) => ({ ...prev, paymentTypes }));
      } else if (revenueMode === "daily_trend") {
        const dailyTrend = Array.isArray(
          res.data.revenue_analytics?.daily_trend
        )
          ? res.data.revenue_analytics.daily_trend
          : [];
        setRevenueData((prev) => ({ ...prev, dailyTrend }));
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to Fetch Revenue Data", "GET");

      // Fallback data
      if (activeRevenueTab === "paymentTypes") {
        setRevenueData((prev) => ({
          ...prev,
          paymentTypes: {
            total_revenue: 1250,
            breakdown: [
              { payment_method: "Credit Card", amount: 650, percent: 52 },
              { payment_method: "Cash", amount: 350, percent: 28 },
              { payment_method: "Digital Wallet", amount: 250, percent: 20 },
            ],
          },
        }));
      } else {
        setRevenueData((prev) => ({
          ...prev,
          dailyTrend: Array.from({ length: 7 }, (_, i) => ({
            date: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
            total: Math.floor(Math.random() * 500) + 100,
          })),
        }));
      }
    } finally {
      setRevenueLoading(false);
    }
  };

  // const getDateRange = () => {
  //   const today = new Date();
  //   if (timeFrame === "today") {
  //     const todayStr = today.toISOString().split("T")[0];
  //     return { from_date: todayStr, to_date: todayStr };
  //   } else if (timeFrame === "weekly") {
  //     const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  //     return {
  //       from_date: weekAgo.toISOString().split("T")[0],
  //       to_date: today.toISOString().split("T")[0],
  //     };
  //   } else if (timeFrame === "monthly") {
  //     const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  //     return {
  //       from_date: monthAgo.toISOString().split("T")[0],
  //       to_date: today.toISOString().split("T")[0],
  //     };
  //   }
  //   return {
  //     from_date: today.toISOString().split("T")[0],
  //     to_date: today.toISOString().split("T")[0],
  //   };
  // };



  const getDateRange = () => {
  const today = new Date();

  // Local date string banane ka function (timezone problem fix)
  const toLocalDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  if (timeFrame === "today") {
    const t = toLocalDate(today);
    return { from_date: t, to_date: t };
  }

  if (timeFrame === "weekly") {
    // Week ka Monday nikalna
    const day = today.getDay(); // 0 = Sunday, 1 = Monday...
    const diff = day === 0 ? 6 : day - 1; // Monday tak ka gap
    const monday = new Date(today);
    monday.setDate(today.getDate() - diff);

    return {
      from_date: toLocalDate(monday),
      to_date: toLocalDate(today),
    };
  }

  if (timeFrame === "monthly") {
    // Is month ki first date
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      from_date: toLocalDate(firstDay),
      to_date: toLocalDate(today),
    };
  }

  // Default — today ka range
  const t = toLocalDate(today);
  return { from_date: t, to_date: t };
};

  const checkForZeroValues = (data) => {
    if (data.currency_status === 0 && data.isExpired === false) {
      setModalConfig({
        title: "Currency Setup Required",
        message:
          "Select your default currency. All staff salaries, incentives, and customer transactions will be processed in this currency.",
        navigateTo: "/user-profile",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_roles === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Roles",
        message:
          "You currently have no Roles. Would you like to create one now?",
        navigateTo: "/staff-role-create",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_staff === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Staff Members",
        message:
          "You currently have no staff members. Would you like to create one now?",
        navigateTo: "/staff-create",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_branches === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Branches",
        message:
          "You currently have no branches. Would you like to create one now?",
        navigateTo: "/branch-create",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_locations === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Locations",
        message:
          "You currently have no locations. Would you like to create one now?",
        navigateTo: "/branch-location-create",
        showCancel: true,
      });
      setShowModal(true);
    }
  };
  useEffect(() => {
    fetchLocationMapData();
  }, []);

  const handleModalConfirm = () => {
    setShowModal(false);
    navigate(modalConfig.navigateTo);
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const paymentColors = [
    "#E63C23",
    "#FF6B4A",
    "#E63C2380",
    "#FF6B4A80",
    "#E63C2360",
    "#FF6B4A60",
  ];

  const processedPaymentData = Array.isArray(revenueData.paymentTypes.breakdown)
    ? revenueData.paymentTypes.breakdown.map((item, index) => ({
        name: item.payment_method || "Unknown",
        value: parseFloat(item.amount || 0),
        percentage: parseFloat(item.percent || 0),
        fill: paymentColors[index % paymentColors.length],
      }))
    : [];

  const processedDailyData = Array.isArray(revenueData.dailyTrend)
    ? revenueData.dailyTrend.map((item) => ({
        name: item.date
          ? new Date(item.date).toLocaleDateString("en-US", {
              weekday: "short",
            })
          : "-",
        value: parseFloat(item.total || 0),
      }))
    : [];

  if (error) {
    return <ErrorAlert onRetry={() => window.location.reload()} />;
  }

  return (
    <>
      <style>{`
        .shimmer {
          background: linear-gradient(
            90deg,
            #f5f5f5 0%,
            #e0e0e0 20%,
            #d0d0d0 40%,
            #f5f5f5 60%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite linear;
          border-radius: 4px;
          position: relative;
          z-index: 1;
        }

        [data-theme="dark"] .shimmer {
          background: linear-gradient(
            90deg,
            #eaeaea 0%,
            #dcdcdc 20%,
            #cccccc 40%,
            #eaeaea 60%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite linear;
          border-radius: 4px;
          position: relative;
          z-index: 1;
        }

        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .btn-primary-600.active {
          background-color: var(--primary-600) !important;
          border-color: var(--primary-600) !important;
          color: white !important;
        }
        
        .btn-primary-600:not(.active) {
          background-color: transparent !important;
          border-color: var(--primary-600) !important;
          color: var(--primary-600) !important;
        }

        .leaflet-container {
  font-family: inherit;
}

.leaflet-popup-content-wrapper {
  background: var(--neutral-50);
  border-radius: 8px;
  box-shadow: var(--shadow-4);
}

.leaflet-popup-tip {
  background: var(--neutral-50);
}

[data-theme="dark"] .leaflet-popup-content-wrapper,
[data-theme="dark"] .leaflet-popup-tip {
  background: var(--neutral-800);
  color: var(--text-primary-light);
}
      `}</style>

      <Breadcrumb title="Dashboard" />

      <div className="container-fluid p-4">
        <div className="row gy-4">
          {/* Subscription Info Banner */}
          {counts &&
            counts.subscription_start_date &&
            counts.subscription_end_date && (
              <div className="col-12">
                <div
                  className="card shadow-4 border-0"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%)",
                    border: "1px solid var(--primary-200)",
                  }}
                >
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-center gap-4 flex-wrap">
                      {/* Plan Name */}
                      <div className="d-flex align-items-center gap-2">
                        <i
                          className="ri-vip-crown-line text-primary-600"
                          style={{ fontSize: "20px" }}
                        ></i>
                        <div>
                          <small
                            className="text-secondary-light d-block"
                            style={{ fontSize: "11px" }}
                          >
                            Current Plan
                          </small>
                          <span
                            className="fw-semibold text-primary-light"
                            style={{ fontSize: "14px" }}
                          >
                            {counts.plan_name || "N/A"}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          width: "2px",
                          height: "40px",
                          background:
                            "linear-gradient(to bottom, transparent, var(--primary-300), transparent)",
                        }}
                      ></div>

                      {/* Subscription Start */}
                      <div className="d-flex align-items-center gap-2">
                        <i
                          className="ri-calendar-line text-primary-600"
                          style={{ fontSize: "20px" }}
                        ></i>
                        <div>
                          <small
                            className="text-secondary-light d-block"
                            style={{ fontSize: "11px" }}
                          >
                            Subscription Start
                          </small>
                          <span
                            className="fw-semibold text-primary-light"
                            style={{ fontSize: "14px" }}
                          >
                            {new Date(
                              counts.subscription_start_date
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          width: "2px",
                          height: "40px",
                          background:
                            "linear-gradient(to bottom, transparent, var(--primary-300), transparent)",
                        }}
                      ></div>

                      {/* Subscription End */}
                      <div className="d-flex align-items-center gap-2">
                        <i
                          className="ri-calendar-check-line text-primary-600"
                          style={{ fontSize: "20px" }}
                        ></i>
                        <div>
                          <small
                            className="text-secondary-light d-block"
                            style={{ fontSize: "11px" }}
                          >
                            Subscription End
                          </small>
                          <span
                            className="fw-semibold text-primary-light"
                            style={{ fontSize: "14px" }}
                          >
                            {new Date(
                              counts.subscription_end_date
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="ms-auto">
                        <span
                          className="badge px-3 py-2"
                          style={{
                            background: counts.isExpired
                              ? "var(--danger-100)"
                              : "var(--success-100)",
                            color: counts.isExpired
                              ? "var(--danger-600)"
                              : "var(--success-600)",
                            border: `1px solid ${
                              counts.isExpired
                                ? "var(--danger-300)"
                                : "var(--success-300)"
                            }`,
                          }}
                        >
                          {counts.isExpired ? "Expired" : "Active"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Time Frame Filter */}
          <div className="col-12 d-flex justify-content-end mb-16">
            <div style={{ width: "200px" }}>
              <CustomSelect
                options={timeFrameOptions}
                value={timeFrameOptions.find((opt) => opt.value === timeFrame)}
                onChange={(selected) =>
                  setTimeFrame(selected?.value || "today")
                }
                placeholder="Select Period"
                isSearchable={false}
                isClearable={false}
                className="select-custom"
              />
            </div>
          </div>

          {/* Cards Section */}
          {cardsLoading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="col-xl-3 col-lg-6 col-md-6 col-12">
                <ShimmerCard />
              </div>
            ))
          ) : (
            <>
              {[
                {
                  label: `Total Vehicles Parked ${
                    timeFrame === "today"
                      ? "Today"
                      : timeFrame === "weekly"
                      ? "This Week"
                      : "This Month"
                  }`,
                  value: initialData?.vehicles_parked ?? 0,
                  img: carImage,
                  trend: "",
                  trendClass: "",
                },
                {
                  label: `Current Occupancy ${
                    timeFrame === "today"
                      ? "Today"
                      : timeFrame === "weekly"
                      ? "This Week"
                      : "This Month"
                  }`,
                  value: `${initialData?.current_occupancy ?? 0}`,
                  img: usersImage,
                  trend: "",
                  trendClass: "",
                },
                {
                  label: `Active Sessions ${
                    timeFrame === "today"
                      ? "Today"
                      : timeFrame === "weekly"
                      ? "This Week"
                      : "This Month"
                  }`,
                  value: initialData?.active_sessions ?? 0,
                  img: buildingImage,
                  trend: "",
                  trendClass: "",
                },
                {
                  label: `Revenue ${
                    timeFrame === "today"
                      ? "Today"
                      : timeFrame === "weekly"
                      ? "This Week"
                      : "This Month"
                  }`,
                  value: `${currencyCode || ""} ${
                    initialData?.revenue_generated?.[0]?.total || 0
                  }`,
                  img: locationImage,
                  trend: "",
                  trendClass: "",
                },
              ].map((item, index) => (
                <div key={index} className="col-xl-3 col-lg-6 col-md-6 col-12">
                  <div
                    className="card shadow-4 h-100"
                    style={{ border: "2px solid var(--border-card)" }}
                  >
                    <div className="card-body p-3 d-flex align-items-center">
                      <div
                        className="d-flex justify-content-center align-items-center rounded-circle me-3"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <img
                          src={item.img}
                          alt={item.label}
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                      <div className="text-start">
                        <p className="fw-bold mb-1 text-secondary-light text-xs">
                          {item.label}
                        </p>
                        <h5 className="fw-bold mb-1 text-primary-light text-md">
                          {item.value}
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Occupancy Overview Section */}
        <div className="row gy-4 mt-4">
          <div className="col-lg-6 col-12">
            {occupancyLoading ? (
              <ShimmerChart />
            ) : (
              <div
                className="card shadow-4 h-100 rounded-8"
                style={{ border: "2px solid var(--border-card)" }}
              >
                <div className="card-body p-24">
                  <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                    <h6 className="mb-0 fw-bold text-md text-primary-light">
                      Occupancy Overview
                    </h6>
                    <div className="d-flex align-items-center gap-3">
                      <div className="btn-group">
                        <button
                          className={`btn btn-primary-600 ${
                            activeOccupancyTab === "byLot" ? "active" : ""
                          }`}
                          onClick={() => setActiveOccupancyTab("byLot")}
                        >
                          By Location
                        </button>
                        <button
                          className={`btn btn-primary-600 ${
                            activeOccupancyTab === "hourly" ? "active" : ""
                          }`}
                          onClick={() => setActiveOccupancyTab("hourly")}
                        >
                          Hourly Trend
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="chart-container">
                    {activeOccupancyTab === "byLot" ? (
                      <div style={{ minHeight: "360px" }}>
                        {occupancyData.lotData.length === 0 ? (
                          <div className="d-flex align-items-center justify-content-center h-100 text-secondary-light text-align-center">
                            No location data available
                          </div>
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                width: "100%",
                                overflowX: "auto",
                                overflowY: "hidden",
                              }}
                              className="hide-scrollbar"
                            >
                              <div
                                style={{
                                  width: `${Math.max(
                                    100,
                                    occupancyData.lotData.length * 20
                                  )}%`,
                                  minWidth: "100%",
                                  height: "360px",
                                }}
                              >
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={occupancyData.lotData.map((item) => ({
                                      name: item.name,
                                      occupied: item.occupied,
                                      available: item.total - item.occupied,
                                      percentage: item.percentage,
                                      total: item.total,
                                    }))}
                                    margin={{
                                      top: 20,
                                      right: 30,
                                      left: 20,
                                      bottom: 60,
                                    }}
                                  >
                                    <defs>
                                      <linearGradient
                                        id="occupiedGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="5%"
                                          stopColor="var(--primary-800)"
                                          stopOpacity={0.8}
                                        />
                                        <stop
                                          offset="95%"
                                          stopColor="var(--primary-800)"
                                          stopOpacity={0.3}
                                        />
                                      </linearGradient>
                                      <linearGradient
                                        id="availableGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                      >
                                        <stop
                                          offset="5%"
                                          stopColor="var(--primary-500)"
                                          stopOpacity={0.8}
                                        />
                                        <stop
                                          offset="95%"
                                          stopColor="var(--primary-500)"
                                          stopOpacity={0.3}
                                        />
                                      </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                      strokeDasharray="3 3"
                                      stroke="var(--border-color)"
                                      strokeOpacity={0.3}
                                    />
                                    <XAxis
                                      dataKey="name"
                                      tick={{
                                        fill: "var(--text-secondary-light)",
                                        fontSize: "var(--font-sm)",
                                      }}
                                      angle={0}
                                      textAnchor="middle"
                                      height={80}
                                      interval={0}
                                    />
                                    <YAxis
                                      tick={{
                                        fill: "var(--text-secondary-light)",
                                        fontSize: "var(--font-sm)",
                                      }}
                                    />
                                    <Tooltip
                                      contentStyle={{
                                        background: "var(--neutral-50)",
                                        border: "1px solid var(--border-color)",
                                        borderRadius: "var(--rounded-8)",
                                        boxShadow: "var(--shadow-4)",
                                      }}
                                      formatter={(value, name, props) => {
                                        if (name === "occupied")
                                          return [
                                            `${value} spaces`,
                                            "Occupied",
                                          ];
                                        if (name === "available")
                                          return [
                                            `${value} spaces`,
                                            "Available",
                                          ];
                                        return [value, name];
                                      }}
                                      labelFormatter={(label) =>
                                        `Location: ${label}`
                                      }
                                    />
                                    <Legend />
                                    <Bar
                                      dataKey="occupied"
                                      stackId="a"
                                      fill="url(#occupiedGradient)"
                                      name="Occupied"
                                      radius={[0, 0, 0, 0]}
                                    />
                                    <Bar
                                      dataKey="available"
                                      stackId="a"
                                      fill="url(#availableGradient)"
                                      name="Available"
                                      radius={[4, 4, 0, 0]}
                                    />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ minHeight: "360px" }}>
                        {occupancyData.hourlyData.length === 0 ? (
                          <div className="d-flex align-items-center justify-content-center h-100 text-secondary-light text-align-center">
                            No hourly data available
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={360}>
                            <AreaChart
                              data={occupancyData.hourlyData}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 10,
                                bottom: 10,
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="occupancyAreaGradient"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#E63C23"
                                    stopOpacity={0.6}
                                  />
                                  <stop
                                    offset="50%"
                                    stopColor="#FF6B4A"
                                    stopOpacity={0.3}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#FF6B4A"
                                    stopOpacity={0.1}
                                  />
                                </linearGradient>
                                <filter id="shadow">
                                  <feDropShadow
                                    dx="0"
                                    dy="2"
                                    stdDeviation="4"
                                    floodColor="#E63C23"
                                    floodOpacity="0.2"
                                  />
                                </filter>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="var(--border-color)"
                                strokeOpacity={0.3}
                              />
                              <XAxis
                                dataKey="hour"
                                tick={{
                                  fill: "var(--text-secondary-light)",
                                  fontSize: "var(--font-sm)",
                                }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                tick={{
                                  fill: "var(--text-secondary-light)",
                                  fontSize: "var(--font-sm)",
                                }}
                                domain={[0, "dataMax + 1"]}
                                axisLine={false}
                                tickLine={false}
                                allowDecimals={false}
                              />
                              <Tooltip
                                contentStyle={{
                                  background: "var(--neutral-50)",
                                  border: "1px solid var(--border-color)",
                                  borderRadius: "var(--rounded-8)",
                                  boxShadow: "var(--shadow-4)",
                                }}
                                formatter={(v, name, props) => [
                                  `${v} vehicles`,
                                  props.payload.isPeak
                                    ? "Peak Occupancy"
                                    : "Normal Occupancy",
                                ]}
                                labelFormatter={(hour) => `Time: ${hour}`}
                              />
                              <Legend
                                content={() => (
                                  <div className="d-flex justify-content-center gap-4 mt-3">
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        style={{
                                          width: 12,
                                          height: 12,
                                          background: "var(--primary-600)",
                                          borderRadius: "50%",
                                          boxShadow:
                                            "0 0 8px var(--primary-600)40",
                                        }}
                                      ></div>
                                      <small className="text-secondary-light">
                                        Hourly Occupancy %
                                      </small>
                                    </div>
                                  </div>
                                )}
                              />
                              <Area
                                type="monotone"
                                dataKey="value"
                                stroke="#E63C23"
                                strokeWidth={3}
                                fill="url(#occupancyAreaGradient)"
                                dot={{
                                  r: 4,
                                  fill: "#E63C23",
                                  strokeWidth: 2,
                                  stroke: "var(--neutral-50)",
                                  filter: "url(#shadow)",
                                }}
                                activeDot={{
                                  r: 6,
                                  fill: "#E63C23",
                                  stroke: "var(--neutral-50)",
                                  strokeWidth: 3,
                                  filter: "url(#shadow)",
                                }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Revenue Analytics Section */}
          <div className="col-lg-6 col-12">
            {revenueLoading ? (
              <ShimmerChart />
            ) : (
              <div
                className="card shadow-4 h-100 rounded-8"
                style={{ border: "2px solid var(--border-card)" }}
              >
                <div className="card-body p-24">
                  <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                    <h6 className="mb-0 fw-bold text-md text-primary-light">
                      Revenue Analytics
                    </h6>
                    <div className="btn-group">
                      <button
                        className={`btn btn-primary-600 ${
                          activeRevenueTab === "dailyTrend" ? "active" : ""
                        }`}
                        onClick={() => setActiveRevenueTab("dailyTrend")}
                      >
                        Daily Trend
                      </button>
                      <button
                        className={`btn btn-primary-600 ${
                          activeRevenueTab === "paymentTypes" ? "active" : ""
                        }`}
                        onClick={() => setActiveRevenueTab("paymentTypes")}
                      >
                        Payment Sources
                      </button>
                    </div>
                  </div>
                  <div className="chart-container">
                    {activeRevenueTab === "paymentTypes" ? (
                      <div style={{ minHeight: "360px" }}>
                        {processedPaymentData.length === 0 ? (
                          <div
                            className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                            style={{ minHeight: "360px" }}
                          >
                            No payment data available
                          </div>
                        ) : (
                          <div className="row h-100">
                            <div className="col-lg-7 col-12">
                              <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                  <Pie
                                    data={processedPaymentData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={3}
                                    dataKey="value"
                                  >
                                    {processedPaymentData.map(
                                      (entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={entry.fill}
                                          stroke="var(--neutral-50)"
                                          strokeWidth={2}
                                        />
                                      )
                                    )}
                                  </Pie>
                                  <Tooltip
                                    contentStyle={{
                                      background: "var(--neutral-50)",
                                      border: "1px solid var(--border-color)",
                                      borderRadius: "var(--rounded-12)",
                                      boxShadow: "var(--shadow-4)",
                                    }}
                                    formatter={(value, name) => [
                                      `${currencyCode} ${value.toLocaleString()}`,
                                      name,
                                    ]}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="col-lg-5 col-12 d-flex flex-column justify-content-center">
                              <div className="mb-4 text-align-center">
                                <h6 className="fw-bold mb-1 text-md text-primary-light">
                                  Total Revenue
                                </h6>
                                <span
                                  className="fw-bold mb-0"
                                  style={{
                                    fontSize: "var(--font-2xl)",
                                    background:
                                      "linear-gradient(135deg, var(--primary-600), var(--success-600))",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                  }}
                                >
                                  {currencyCode}{" "}
                                  {revenueData.paymentTypes.total_revenue || 0}
                                </span>
                              </div>
                              <div className="d-flex flex-column gap-3">
                                {processedPaymentData.map((item, index) => (
                                  <div
                                    key={index}
                                    className="d-flex align-items-center justify-content-between p-3"
                                    style={{
                                      background:
                                        "linear-gradient(135deg, var(--neutral-50)10, var(--neutral-50)05)",
                                      borderRadius: "var(--rounded-12)",
                                      border: "1px solid var(--border-color)",
                                      transition: "all 0.3s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "translateY(-2px)";
                                      e.currentTarget.style.boxShadow =
                                        "var(--shadow-4)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                      e.currentTarget.style.boxShadow = "none";
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-3">
                                      <div
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "50%",
                                          background: item.fill,
                                          boxShadow: `0 0 10px ${item.fill}40`,
                                        }}
                                      ></div>
                                      <span className="fw-semibold text-sm text-primary-light">
                                        {item.name}
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="fw-bold text-sm text-primary-light">
                                        {currencyCode}{" "}
                                        {item.value.toLocaleString()}
                                      </span>
                                      <span
                                        className="badge"
                                        style={{
                                          background: `${item.fill}20`,
                                          color: item.fill,
                                          fontSize: "var(--font-xs)",
                                          padding: "4px 8px",
                                          borderRadius: "var(--rounded-6)",
                                          border: `1px solid ${item.fill}40`,
                                        }}
                                      >
                                        {item.percentage.toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ minHeight: "360px" }}>
                        {processedDailyData.length === 0 ? (
                          <div
                            className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                            style={{ minHeight: "360px" }}
                          >
                            No daily trend data available
                          </div>
                        ) : (
                          <>
                            <div className="row mb-4">
                              <div className="col-4">
                                <div
                                  className="text-align-center p-3"
                                  style={{
                                    background:
                                      "linear-gradient(135deg, var(--success-50), var(--success-100))",
                                    borderRadius: "var(--rounded-12)",
                                    border: "1px solid var(--success-border)",
                                  }}
                                >
                                  <div className="text-secondary-light text-xs">
                                    Avg Daily
                                  </div>
                                  <div className="fw-bold text-primary-light text-md">
                                    {currencyCode}{" "}
                                    {(
                                      processedDailyData.reduce(
                                        (sum, item) => sum + item.value,
                                        0
                                      ) / processedDailyData.length
                                    ).toLocaleString(undefined, {
                                      maximumFractionDigits: 0,
                                    })}
                                  </div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div
                                  className="text-align-center p-3"
                                  style={{
                                    background:
                                      "linear-gradient(135deg, var(--primary-50), var(--primary-100))",
                                    borderRadius: "var(--rounded-12)",
                                    border: "1px solid var(--primary-600)40",
                                  }}
                                >
                                  <div className="text-secondary-light text-xs">
                                    Peak Day
                                  </div>
                                  <div className="fw-bold text-primary-light text-md">
                                    {currencyCode}{" "}
                                    {Math.max(
                                      ...processedDailyData.map(
                                        (item) => item.value
                                      )
                                    ).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="col-4">
                                <div
                                  className="text-align-center p-3"
                                  style={{
                                    background:
                                      "linear-gradient(135deg, var(--warning-50), var(--warning-100))",
                                    borderRadius: "var(--rounded-12)",
                                    border: "1px solid var(--warning-600)40",
                                  }}
                                >
                                  <div className="text-secondary-light text-xs">
                                    Total
                                  </div>
                                  <div className="fw-bold text-primary-light text-md">
                                    {currencyCode}{" "}
                                    {processedDailyData
                                      .reduce(
                                        (sum, item) => sum + item.value,
                                        0
                                      )
                                      .toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                              <LineChart
                                data={processedDailyData}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 10,
                                  bottom: 10,
                                }}
                              >
                                <defs>
                                  <linearGradient
                                    id="lineGradient"
                                    x1="0"
                                    y1="0"
                                    x2="1"
                                    y2="0"
                                  >
                                    <stop
                                      offset="0%"
                                      stopColor="#E63C23"
                                      stopOpacity={1}
                                    />
                                    <stop
                                      offset="100%"
                                      stopColor="#FF6B4A"
                                      stopOpacity={1}
                                    />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke="var(--border-color)"
                                  strokeOpacity={0.3}
                                />
                                <XAxis
                                  dataKey="name"
                                  tick={{
                                    fill: "var(--text-secondary-light)",
                                    fontSize: "var(--font-sm)",
                                  }}
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <YAxis
                                  tick={{
                                    fill: "var(--text-secondary-light)",
                                    fontSize: "var(--font-sm)",
                                  }}
                                  tickFormatter={(v) =>
                                    `${v / 1000}k`
                                  }
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <Tooltip
                                  contentStyle={{
                                    background: "var(--neutral-50)",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: "var(--rounded-12)",
                                    boxShadow: "var(--shadow-4)",
                                  }}
                                  formatter={(v) => [
                                    `${currencyCode}${v.toLocaleString()}`,
                                    "Revenue",
                                  ]}
                                  labelStyle={{
                                    color: "var(--text-primary-light)",
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="value"
                                  stroke="url(#lineGradient)"
                                  strokeWidth={4}
                                  dot={{
                                    r: 6,
                                    fill: "#E63C23",
                                    strokeWidth: 3,
                                    stroke: "var(--neutral-50)",
                                  }}
                                  activeDot={{
                                    r: 8,
                                    fill: "#E63C23",
                                    stroke: "var(--neutral-50)",
                                    strokeWidth: 4,
                                    boxShadow: "0 0 15px #E63C2350",
                                  }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Staff Activity Section */}
        <div className="row gy-4 mt-4">
          <div className="col-lg-6 col-12">
            {staffLoading || cardsLoading ? (
              <ShimmerChart />
            ) : (
              <div
                className="card shadow-4 h-100 rounded-8"
                style={{ border: "2px solid var(--border-card)" }}
              >
                <div className="card-body p-24">
                  <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                    <h6 className="mb-0 fw-bold text-md text-primary-light">
                      Staff Activity
                    </h6>
                  </div>
                  <div className="chart-container">
                    {staffData.length === 0 ? (
                      <div
                        className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                        style={{ minHeight: "300px" }}
                      >
                        No staff activity data available
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={staffData.map((staff) => ({
                            name: staff.staff_name || "Unknown",
                            jobs: parseInt(staff.total_jobs) || 0,
                          }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                        >
                          <defs>
                            <linearGradient
                              id="staffGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#E63C23"
                                stopOpacity={0.8}
                              />
                              <stop
                                offset="95%"
                                stopColor="#FF6B4A"
                                stopOpacity={0.6}
                              />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--border-color)"
                            strokeOpacity={0.3}
                          />
                          <XAxis
                            dataKey="name"
                            tick={{
                              fill: "var(--text-primary-light)",
                              fontSize: "var(--font-sm)",
                            }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                          />
                          <YAxis
                            tick={{
                              fill: "var(--text-secondary-light)",
                              fontSize: "var(--font-sm)",
                            }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              background: "var(--neutral-50)",
                              border: "1px solid var(--border-color)",
                              borderRadius: "var(--rounded-8)",
                              boxShadow: "var(--shadow-4)",
                            }}
                            formatter={(value) => [value, "Jobs Completed"]}
                          />
                          <Bar
                            dataKey="jobs"
                            fill="url(#staffGradient)"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Location Map Section */}
          <div className="col-lg-6 col-12">
            {mapLoading ? (
              <ShimmerChart />
            ) : (
              <div
                className="card shadow-4 h-100 rounded-8"
                style={{ border: "2px solid var(--border-card)" }}
              >
                <div className="card-body p-24">
                  <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                    <h6 className="mb-0 fw-bold text-md text-primary-light">
                      Locations Geographical Footprint
                    </h6>
                    <span className="badge bg-primary-50 text-primary-600 px-3 py-2">
                      {locationMapData.length} Locations
                    </span>
                  </div>
                  <div
                    style={{
                      height: "400px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid var(--border-color)",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {locationMapData.length === 0 ? (
                      <div className="d-flex align-items-center justify-content-center h-100 text-secondary-light">
                        No location data available
                      </div>
                    ) : (
                      <MapContainer
                        center={[
                          parseFloat(locationMapData[0]?.latitude) || 24.8607,
                          parseFloat(locationMapData[0]?.longitude) || 67.0011,
                        ]}
                        zoom={11}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        {locationMapData.map((loc, idx) => {
                          const lat = parseFloat(loc.latitude);
                          const lng = parseFloat(loc.longitude);

                          if (isNaN(lat) || isNaN(lng)) return null;

                          // Convert polygons to Leaflet format
                          const polygonPositions =
                            loc.polygons?.map((point) => [
                              point.lat,
                              point.lng,
                            ]) || [];

                          return (
                            <React.Fragment key={idx}>
                              {/* Polygon Shape */}
                              {polygonPositions.length > 0 && (
                                <Polygon
                                  positions={polygonPositions}
                                  pathOptions={{
                                    color: "#E63C23",
                                    fillColor: "#FF6B4A",
                                    fillOpacity: 0.2,
                                    weight: 2,
                                  }}
                                />
                              )}

                              {/* Center Marker */}
                              <Marker position={[lat, lng]}>
                                <Popup>
                                  <div style={{ minWidth: "180px" }}>
                                    <strong
                                      style={{
                                        fontSize: "14px",
                                        color: "var(--primary-600)",
                                      }}
                                    >
                                      {loc.location_name}
                                    </strong>
                                    <br />
                                    <small
                                      style={{
                                        color: "var(--text-secondary-light)",
                                      }}
                                    >
                                      {loc.branch_name || "N/A"}
                                    </small>
                                    <hr style={{ margin: "8px 0" }} />
                                    <div style={{ fontSize: "12px" }}>
                                      <div>
                                        <strong>Code:</strong>{" "}
                                        {loc.location_code}
                                      </div>
                                      <div>
                                        <strong>Capacity:</strong>{" "}
                                        {loc.parking_capacity || 0} slots
                                      </div>
                                      <div>
                                        <strong>Status:</strong>{" "}
                                        {loc.is_active
                                          ? "✅ Active"
                                          : "❌ Inactive"}
                                      </div>
                                    </div>
                                    {loc.notes && (
                                      <>
                                        <hr style={{ margin: "8px 0" }} />
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {loc.notes}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </Popup>
                              </Marker>
                            </React.Fragment>
                          );
                        })}
                      </MapContainer>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <Modal show={showModal} onHide={handleModalCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-primary-light fw-bold text-md">{modalConfig.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-primary-light text-sm">{modalConfig.message}</Modal.Body>
        <Modal.Footer>
          {modalConfig.showCancel && (
            <Button variant="outline-secondary" onClick={handleModalCancel} className="btn-modal">
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleModalConfirm}
            className="btn-modal"
            style={{ background: "var(--primary-600)", borderColor: "var(--primary-600)" }}
          >
            {modalConfig.title.includes("Currency")
              ? "Set Currency"
              : modalConfig.title.includes("Roles")
              ? "Create Role"
              : modalConfig.title.includes("Staff")
              ? "Create Staff"
              : modalConfig.title.includes("Branch")
              ? "Create Branch"
              : modalConfig.title.includes("Location")
              ? "Create Location"
              : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal> */}
    </>
  );
};

export default Dashboard;
